import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import {
  createMemberNote,
  getMemberDetail,
  getMemberHistory,
  getMemberNotes,
  getMemberPayments,
  getMemberTerminationPreview,
  getMembers,
  requestMemberTermination,
  revealMemberData,
} from "./api";
import type { MemberListQuery, MemberPaymentQuery } from "./api";
import type { Schemas } from "../workspace/api.generated";

export const memberKeys = {
  all: (sessionEpoch: number) => ["admin", sessionEpoch, "members"] as const,
  lists: (sessionEpoch: number) => [...memberKeys.all(sessionEpoch), "list"] as const,
  list: (sessionEpoch: number, query: MemberListQuery) => (
    [...memberKeys.lists(sessionEpoch), query] as const
  ),
  detail: (sessionEpoch: number, memberId: number) => (
    [...memberKeys.all(sessionEpoch), "detail", memberId] as const
  ),
  payments: (sessionEpoch: number, memberId: number, query: MemberPaymentQuery) => (
    [...memberKeys.detail(sessionEpoch, memberId), "payments", query] as const
  ),
  notes: (sessionEpoch: number, memberId: number) => (
    [...memberKeys.detail(sessionEpoch, memberId), "notes"] as const
  ),
  history: (sessionEpoch: number, memberId: number) => (
    [...memberKeys.detail(sessionEpoch, memberId), "history"] as const
  ),
  terminationPreview: (sessionEpoch: number, memberId: number) => (
    [...memberKeys.detail(sessionEpoch, memberId), "termination-preview"] as const
  ),
};

export function useMemberTerminationPreview(memberId: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: memberKeys.terminationPreview(session.sessionEpoch, memberId),
    queryFn: ({ signal }) => getMemberTerminationPreview(apiClient, memberId, signal),
    enabled: false,
    retry: false,
  });
}

export function useMemberList(query: MemberListQuery) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: memberKeys.list(session.sessionEpoch, query),
    queryFn: ({ signal }) => getMembers(apiClient, query, signal),
  });
}

export function useMemberDetail(memberId: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: memberKeys.detail(session.sessionEpoch, memberId),
    queryFn: ({ signal }) => getMemberDetail(apiClient, memberId, signal),
  });
}

export function useMemberPayments(memberId: number, query: MemberPaymentQuery, enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: memberKeys.payments(session.sessionEpoch, memberId, query),
    queryFn: ({ signal }) => getMemberPayments(apiClient, memberId, query, signal),
    enabled,
  });
}

export function useMemberNotes(memberId: number, enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: memberKeys.notes(session.sessionEpoch, memberId),
    queryFn: ({ signal }) => getMemberNotes(apiClient, memberId, signal),
    enabled,
  });
}

export function useMemberHistory(memberId: number, enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: memberKeys.history(session.sessionEpoch, memberId),
    queryFn: ({ signal }) => getMemberHistory(apiClient, memberId, signal),
    enabled,
  });
}

export function useCreateMemberNote(memberId: number) {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: Schemas["CreateMemberCareNoteRequest"]) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await createMemberNote(apiClient, memberId, input, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: async (note) => {
      queryClient.setQueryData<Schemas["MemberCareNoteView"][]>(
        memberKeys.notes(session.sessionEpoch, memberId),
        (current) => current ? [...current, note] : [note],
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: memberKeys.notes(session.sessionEpoch, memberId) }),
        queryClient.invalidateQueries({ queryKey: memberKeys.detail(session.sessionEpoch, memberId) }),
      ]);
    },
  });
}

export function useRevealMemberData(memberId: number) {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: Schemas["UnmaskMemberRequest"]) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await revealMemberData(apiClient, memberId, input, request.controller.signal);
      } finally {
        request.release();
      }
    },
  });
}

export function useRequestMemberTermination(memberId: number) {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: Schemas["RequestTerminationRequest"]) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await requestMemberTermination(apiClient, memberId, input, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: memberKeys.detail(session.sessionEpoch, memberId) }),
        queryClient.invalidateQueries({ queryKey: memberKeys.lists(session.sessionEpoch) }),
        queryClient.invalidateQueries({ queryKey: ["admin", session.sessionEpoch, "refunds"] }),
      ]);
    },
  });
}
